import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Plus, Edit2, Trash2, Wifi } from 'lucide-react';

const PPPoEPlansPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [routers, setRouters] = useState([]);
  const [importedProfiles, setImportedProfiles] = useState([]);
  const [pools, setPools] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    download_speed: '',
    upload_speed: '',
    price_monthly: '',
    grace_days: '3',
    description: '',
    router_id: '',
    linked_profile_id: '',
    local_address_pool: '',
    remote_address_pool: ''
  });

  useEffect(() => {
    loadPlans();
    loadRouters();
  }, []);

  const loadRouters = async () => {
    try {
      const response = await api.get('/api/routers');
      setRouters(response.data);
    } catch (error) {
      console.error('Error cargando routers:', error);
    }
  };

  const loadImportedProfiles = async (routerId) => {
    if (!routerId) {
      setImportedProfiles([]);
      return;
    }
    try {
      const response = await api.get(`/api/pppoe/imported-profiles/${routerId}`);
      setImportedProfiles(response.data.profiles);
    } catch (error) {
      console.error('Error cargando profiles importados:', error);
      setImportedProfiles([]);
    }
  };


  const loadPools = async (routerId) => {
    if (!routerId) return;
    try {
      const response = await api.get(`/api/pppoe/pools/${routerId}`);
      setPools(response.data.pools);
    } catch (error) {
      console.error('Error cargando pools:', error);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await api.get('/api/pppoe/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Error cargando planes:', error);
      alert('Error al cargar planes PPPoE');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price_monthly: parseFloat(formData.price_monthly),
        grace_days: parseInt(formData.grace_days)
      };

      if (editingPlan) {
        await api.put(`/api/pppoe/plans/${editingPlan.id}`, data);
        alert('✅ Plan actualizado exitosamente');
      } else {
        await api.post('/api/pppoe/plans', data);
        alert('✅ Plan creado exitosamente');
      }

      setShowModal(false);
      setEditingPlan(null);
      setFormData({
        name: '',
        download_speed: '',
        upload_speed: '',
        price_monthly: '',
        grace_days: '3',
        description: ''
      });
      loadPlans();
    } catch (error) {
      console.error('Error guardando plan:', error);
      alert('❌ Error al guardar plan');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      download_speed: plan.download_speed,
      upload_speed: plan.upload_speed,
      price_monthly: plan.price_monthly.toString(),
      grace_days: plan.grace_days.toString(),
      description: plan.description || '',
      router_id: plan.router_id || '',
      linked_profile_id: plan.linked_profile_id || ''
    });
    if (plan.router_id) {
      loadImportedProfiles(plan.router_id);
    }
    setShowModal(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('¿Estás seguro de eliminar este plan?')) return;

    try {
      await api.delete(`/api/pppoe/plans/${planId}`);
      alert('✅ Plan eliminado');
      loadPlans();
    } catch (error) {
      console.error('Error eliminando plan:', error);
      alert(error.response?.data?.detail || '❌ Error al eliminar plan');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/pppoe')}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
            >
              <ArrowLeft className={isDark ? 'text-white' : 'text-gray-900'} />
            </button>
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Planes PPPoE
              </h1>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Gestiona los planes de servicio de internet fijo
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingPlan(null);
              setFormData({
                name: '',
                download_speed: '',
                upload_speed: '',
                price_monthly: '',
                grace_days: '3',
                description: '',
                router_id: '',
                linked_profile_id: ''
              });
              setImportedProfiles([]);
              setShowModal(true);
            }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nuevo Plan
          </button>
        </div>

        {/* Plans Grid */}
        {plans.length === 0 ? (
          <div className={`text-center py-12 rounded-xl ${
            isDark ? 'bg-gray-800/50' : 'bg-white'
          } shadow-lg`}>
            <Wifi className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              No hay planes creados
            </h3>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              Crea tu primer plan PPPoE para empezar
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`p-6 rounded-xl shadow-lg border-2 transition-all ${
                  isDark
                    ? 'bg-gray-800/50 border-gray-700 hover:border-blue-500'
                    : 'bg-white border-gray-200 hover:border-blue-400'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {plan.name}
                    </h3>
                    <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      ${plan.price_monthly}
                      <span className="text-sm font-normal"> /mes</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(plan)}
                      className={`p-2 rounded-lg transition-colors ${
                        isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      }`}
                    >
                      <Edit2 className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    </button>
                    <button
                      onClick={() => handleDelete(plan.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      }`}
                    >
                      <Trash2 className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className={`flex justify-between ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span>Bajada:</span>
                    <span className="font-semibold">{plan.download_speed}</span>
                  </div>
                  <div className={`flex justify-between ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span>Subida:</span>
                    <span className="font-semibold">{plan.upload_speed}</span>
                  </div>
                  <div className={`flex justify-between ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span>Días de gracia:</span>
                    <span className="font-semibold">{plan.grace_days} días</span>
                  </div>
                  {plan.description && (
                    <p className={`text-sm mt-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {plan.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`max-w-2xl w-full rounded-xl shadow-2xl ${
              isDark ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="p-6">
                <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {editingPlan ? 'Editar Plan' : 'Nuevo Plan PPPoE'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Nombre del Plan *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          isDark 
                            ? 'bg-white border-gray-300 text-gray-900' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="Ej: Plan Básico"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Router *
                      </label>
                      <select
                        required
                        value={formData.router_id}
                        onChange={(e) => {
                          setFormData({...formData, router_id: e.target.value, linked_profile_id: ''});
                          loadImportedProfiles(e.target.value);
                          loadPools(e.target.value);
                        }}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          isDark 
                            ? 'bg-white border-gray-300 text-gray-900' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">Selecciona un router</option>
                        {routers.map(router => (
                          <option key={router.id} value={router.id}>
                            {router.name} ({router.ip_address}:{router.port})
                          </option>
                        ))}
                      </select>
                    </div>

                    {formData.router_id && (
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Ligar con Profile (Opcional)
                        </label>
                        <select
                          value={formData.linked_profile_id}
                          onChange={(e) => setFormData({...formData, linked_profile_id: e.target.value})}
                          className={`w-full px-4 py-2 rounded-lg border ${
                            isDark 
                              ? 'bg-white border-gray-300 text-gray-900' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        >
                          <option value="">Crear profile nuevo</option>
                          {importedProfiles.map(profile => (
                            <option key={profile.id} value={profile.id}>
                              {profile.name}
                              {profile.rate_limit ? ` (${profile.rate_limit})` : ''}
                              {profile.local_address ? ` | Local: ${profile.local_address}` : ''}
                              {profile.remote_address ? ` | Remote: ${profile.remote_address}` : ''}
                            </option>
                          ))}
                        </select>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {importedProfiles.length === 0 
                            ? 'No hay profiles importados. Se creará uno nuevo.' 
                            : 'Selecciona un profile importado o crea uno nuevo'}
                        </p>
                      </div>
                    )}

                    {/* Selectores de Pools */}
                    {formData.router_id && (
                      <>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Local Address Pool *
                          </label>
                          <select
                            required
                            value={formData.local_address_pool}
                            onChange={(e) => setFormData({...formData, local_address_pool: e.target.value})}
                            className={`w-full px-4 py-2 rounded-lg border ${
                              isDark 
                                ? 'bg-white border-gray-300 text-gray-900' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          >
                            <option value="">Selecciona pool para gateway</option>
                            {pools.map(pool => (
                              <option key={pool.name} value={pool.name}>
                                {pool.name} ({pool.ranges})
                              </option>
                            ))}
                          </select>
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            Pool para la IP del router/gateway (normalmente pool de Hotspot)
                          </p>
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Remote Address Pool *
                          </label>
                          <select
                            required
                            value={formData.remote_address_pool}
                            onChange={(e) => setFormData({...formData, remote_address_pool: e.target.value})}
                            className={`w-full px-4 py-2 rounded-lg border ${
                              isDark 
                                ? 'bg-white border-gray-300 text-gray-900' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          >
                            <option value="">Selecciona pool para clientes</option>
                            {pools.map(pool => (
                              <option key={pool.name} value={pool.name}>
                                {pool.name} ({pool.ranges})
                              </option>
                            ))}
                          </select>
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            Pool de IPs para los clientes PPPoE
                          </p>
                        </div>
                      </>
                    )}

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Precio Mensual * ($)
                      </label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        value={formData.price_monthly}
                        onChange={(e) => setFormData({...formData, price_monthly: e.target.value})}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          isDark 
                            ? 'bg-white border-gray-300 text-gray-900' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="299.00"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Velocidad de Bajada *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.download_speed}
                        onChange={(e) => setFormData({...formData, download_speed: e.target.value})}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          isDark 
                            ? 'bg-white border-gray-300 text-gray-900' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="Ej: 10M"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Velocidad de Subida *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.upload_speed}
                        onChange={(e) => setFormData({...formData, upload_speed: e.target.value})}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          isDark 
                            ? 'bg-white border-gray-300 text-gray-900' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="Ej: 5M"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Días de Gracia *
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        value={formData.grace_days}
                        onChange={(e) => setFormData({...formData, grace_days: e.target.value})}
                        className={`w-full px-4 py-2 rounded-lg border ${
                          isDark 
                            ? 'bg-white border-gray-300 text-gray-900' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="3"
                      />
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Días después del vencimiento antes de suspender
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Descripción
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows="3"
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-white border-gray-300 text-gray-900' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="Descripción del plan..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingPlan(null);
                      }}
                      className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                        isDark
                          ? 'bg-gray-700 hover:bg-gray-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                      }`}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all"
                    >
                      {editingPlan ? 'Actualizar' : 'Crear'} Plan
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PPPoEPlansPage;
